import { useTranslate } from '../../application/i18n/LocaleProvider';
import CarDeckCard from './CarDeckCard';
import CarSpecPlate from './CarSpecPlate';
import CarDeckControls from './CarDeckControls';
import CarDeckHint from './CarDeckHint';

/**
 * The deck itself: the card in front, the ones seated behind it, and the two
 * ways of stepping through them that are not a finger (SPEC-004).
 *
 * Lifted out of `CarDetailScreen` when that file reached the repo's length
 * limit, and this is the seam rather than an arbitrary cut: everything above it
 * on that screen is the page's ORDER — the way back, the actions, the bands, the
 * viewer — and everything here is one stage with a gesture running under it.
 *
 * What stays behind is the one thing that has to: the custom properties a drag
 * writes live on the `<article>`, because the card's translate, its tilt and the
 * stack's rise are all CSS expressions over a single set of them, and that is
 * what keeps a drag at one style write per frame instead of one per moving part.
 * This component reads the frame it was given; it never writes one.
 */
export default function CarDeck({ car, deck, seats, dismissHint, handlers, onStep, onOpenPicture }) {
    const t = useTranslate();

    return (
        // Named and counted, because a deck browsed by dragging says nothing at
        // all to a reader who is not looking at it.
        <section
            className="car-deck"
            aria-label={t('a11y.deckPosition', { index: deck.index + 1, total: deck.total })}
            {...handlers}
        >
            {/* Said once per device, on the first car a reader opens, and never
                again. The stack underneath says there is more of this; only this
                says how to reach it. */}
            <CarDeckHint dismissed={dismissHint} />

            {/* The slot the front card sits in, and the frame the cards behind it
                are measured against. It does NOT move: the card is what a drag
                translates, and a stack travelling with the card it stands behind
                would be one object sliding rather than a card being dealt off a
                deck. */}
            <div className="car-deck__slot">
                {/* ONE KEYED LIST, and every car that can be on screen in the
                    next moment already has a seat. Keyed by car, so a step MOVES
                    the node that was behind into the front role rather than
                    rewriting the node in front — rewriting a live `<img>`'s `src`
                    tears its frame down and re-attaches the bitmap, and at 1440px
                    that repaint is visible.

                    Painting order is `--seat-depth`, not document order. */}
                {seats.map((seat) => (
                    <div
                        className={`car-deck__seat car-deck__seat--${seat.role}`}
                        key={seat.car.id}
                        style={{
                            '--seat-depth': seat.seatDepth,
                            '--livery': `var(--livery-${seat.car.livery})`,
                        }}
                        aria-hidden={seat.role === 'front' ? undefined : 'true'}
                        inert={seat.role !== 'front'}
                    >
                        <CarDeckCard car={seat.car} role={seat.role} onOpen={onOpenPicture} />
                    </div>
                ))}
            </div>

            {/* THE FOUR FIGURES, drawn ONCE for the car in hand rather than
                once per seat.

                They sat on the card until the plate had to roll between cars
                (SPEC-004 D-61). A plate inside a seat is a different element on
                every deal — the seat behind is promoted and its own plate is
                already showing its own numbers — so the figures could only ever
                cut. Out here there is one plate for the whole deck, it survives
                the deal, and `CarSpecPlateCell` rolls each figure from what it
                was to what it is. */}
            <div className="car-deck__paper">
                <span className="deck-card__hairline heat-hairline" aria-hidden="true" />
                <CarSpecPlate car={car} />
            </div>

            <CarDeckControls deck={deck} onStep={onStep} />
        </section>
    );
}
